import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FEATURES from 'global/features';
import { PLANS } from 'global/plans';
import { roles } from 'global/rolesAndPermissions';
import { routePaths } from 'global/routePaths';
import { useFeatureList } from 'store/useFeatureStore';
import { useIntegrationStore } from 'store/useIntegrationStore';
import { useUserObject } from 'store/useUserStore';

export function useAllowedReportList() {
  const { t } = useTranslation(['common', 'reports']);

  const [allowedReportData, setAllowedReportData] = useState<any>([]);
  const userStore = useUserObject();
  const featureList: any = useFeatureList();
  const integrationListData: any = useIntegrationStore(state => state.integrationList);
  const xeroIntegrationData =
    integrationListData && integrationListData.length > 0
      ? integrationListData.find((tab: any) => tab.key === 'xero')
      : { is_connected: false };

  useEffect(() => {
    const reportsList = [
      {
        name: t('reports:reportsByUserOrGroup'),
        description: t('reports:reportsByUserOrGroupDesc'),
        pathname: routePaths.reports.employee,
        isVisible: true,
        isNew: false,
      },
      {
        name: t('reports:reportByJob'),
        description: t('reports:reportByJobDesc'),
        pathname: routePaths.reports.job,
        isVisible: userStore?.userInfo?.roleId !== roles.EMPLOYEE && featureList[FEATURES.jobs],
        isNew: false,
      },
      {
        name: t('reports:reportByTask'),
        description: t('reports:reportByTaskDesc'),
        pathname: routePaths.reports.task,
        isVisible: userStore?.userInfo?.roleId !== roles.EMPLOYEE && featureList[FEATURES.jobs],
        isNew: false,
      },
      {
        name: t('reports:reportByDateRange'),
        description: t('reports:reportByDateRangeDesc'),
        pathname: routePaths.reports.dateRange,
        isVisible: true,
        isNew: false,
      },
      {
        name: t('reports:reportByTotalsOnly'),
        description: t('reports:reportsByTotalsOnlyDesc'),
        pathname: routePaths.reports.totalOnly,
        isVisible: userStore?.userInfo?.roleId === roles.ADMIN,
        isNew: false,
      },
      {
        name: t('reports:reportsByMileage'),
        description: t('reports:reportsByMileageDesc'),
        pathname: routePaths.reports.mileage,
        isVisible: userStore?.userInfo?.roleId === roles.ADMIN,
        isNew: false,
      },
      {
        name: t('reports:breakReport'),
        description: t('reports:breakReportDesc'),
        pathname: routePaths.reports.break,
        isVisible:
          (userStore?.userInfo?.roleId === roles.ADMIN || userStore?.userInfo?.roleId === roles.MANAGER) &&
          userStore?.companyInfo?.allowManualBreak,
        isNew: false,
      },
      {
        name: t('reports:dailySignOffs'),
        description: t('reports:dailySignOffsDesc'),
        pathname: routePaths.reports.californiaDailySignOffReport,
        isVisible: featureList[FEATURES.cali_break_policy],
        isNew: false,
      },
      {
        name: t('reports:premiumPayReport'),
        description: t('reports:premiumPayReportDesc'),
        pathname: routePaths.reports.californiaPremiumPayReport,
        isVisible: featureList[FEATURES.cali_break_policy],
        isNew: false,
      },
      {
        name: t('reports:scheduledVsHoursWorked'),
        description: t('reports:scheduledVsHoursWorkedDesc'),
        pathname: routePaths.reports.scheduleVsHours,
        isVisible: featureList[FEATURES.scheduling],
        isNew: false,
      },
      {
        name: t('reports:dailyFlatRateByUser'),
        description: t('reports:dailyFlatRateByUserDesc'),
        pathname: routePaths.reports.dailyFlatRateByEmployee,
        isVisible: userStore?.userInfo?.roleId !== roles.EMPLOYEE,
        isNew: false,
      },
      {
        name: t('reports:dailyFlatRateByTotal'),
        description: t('reports:dailyFlatRateByTotalDesc'),
        pathname: routePaths.reports.dailyFlatRateByTotal,
        isVisible: userStore?.userInfo?.roleId !== roles.EMPLOYEE,
        isNew: false,
      },
      {
        name: t('reports:weekTotals'),
        description: t('reports:weekTotalsDesc'),
        pathname: routePaths.reports.weekTotals,
        isVisible: true,
        isNew: false,
      },
      {
        name: t('reports:xeroReport'),
        description: t('reports:xeroReporDesc'),
        pathname: routePaths.reports.xeroReport,
        isVisible: xeroIntegrationData.is_connected,
        isNew: false,
      },
      {
        name: t('reports:segmentedTrackingReports'),
        description: t('reports:segmentedTrackingReportsDesc'),
        pathname: routePaths.reports.segmentedTrackingReport,
        isVisible: featureList[FEATURES.segmented_tracking],
        isNew: false,
      },
      {
        name: t('reports:scheduleReportByUser'),
        description: t('reports:scheduleReportByUserDesc'),
        pathname: routePaths.reports.scheduleReportByUser,
        isVisible: true,
        isNew: false,
      },
      {
        name: t('reports:customFieldsReports'),
        description: t('reports:customFieldsReportsDesc'),
        pathname: routePaths.reports.customFields.templates,
        isVisible: featureList[FEATURES.custom_fields],
        isNew: false,
      },
      {
        name: t('reports:dataExports'),
        description: t('reports:dataExportsDescription'),
        pathname: routePaths.reports.dataExports,
        isVisible:
          userStore?.userInfo?.roleId === roles.ADMIN && userStore?.companyInfo?.plan?.toLowerCase() !== PLANS.basic,
        isNew: false,
      },
      {
        name: t('reports:flaggedEntriesReport'),
        description: t('reports:flaggedEntriesReportDesc'),
        pathname: routePaths.reports.flaggedEntries,
        isVisible:
          userStore?.userInfo?.roleId === roles.ADMIN && userStore?.companyInfo?.plan?.toLowerCase() !== PLANS.basic,
        isNew: false,
      },
      {
        name: t('reports:reportByJobsTotalOnly'),
        description: t('reports:reportByJobsTotalOnlyDesc'),
        pathname: routePaths.reports.reportByJobsTotalOnly,
        isVisible:
          userStore?.userInfo?.roleId === roles.ADMIN && userStore?.companyInfo?.plan?.toLowerCase() !== PLANS.basic,
        isNew: true,
      },
    ];

    const newSidebar = reportsList.filter(({ isVisible }: any) => {
      return isVisible;
    });

    setAllowedReportData(newSidebar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStore, integrationListData]);

  return { allowedReportData };
}
